import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { BookOpen, Download, CheckCircle2, FileText } from "lucide-react";
import { BrandMark } from "@/components/site/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  "I'm receiving keys to a BTO",
  "I just bought a Resale flat",
  "Renovating my current home",
  "Just curious / researching",
];

export const LeadMagnet = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        property_type: propertyType || null,
        consent: true,
      });
      setDone(true);
      toast.success("Sent! Your download is starting…");

      // Trigger browser download — always use our public API base, not the
      // (possibly-internal) absolute URL the backend echoed back.
      const downloadHref = `${API}/leads/download`;
      const a = document.createElement("a");
      a.href = downloadHref;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = "Noah+Rayn — 2026 BTO Renovation Cost Guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      toast.error("Couldn't generate your guide right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="guide"
      data-testid="lead-magnet-section"
      className="relative bg-[color:var(--nr-bg)] py-24 md:py-32 overflow-hidden"
    >
      <Toaster position="top-center" richColors />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-[#2D2422] to-[#3a2e2b] text-white">
          {/* Decorative accent */}
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[color:var(--nr-accent)]/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[color:var(--nr-accent)]/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16 p-8 sm:p-12 lg:p-16">
            {/* Left: pitch */}
            <div className="lg:col-span-7 reveal">
              <p
                className="overline mb-5"
                style={{ color: "#E6A079" }}
                data-testid="lead-magnet-overline"
              >
                Free download · 14 pages
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                The{" "}
                <em className="italic font-light" style={{ color: "#E6A079" }}>
                  2026 BTO &amp; Resale
                </em>
                <br />
                Renovation Cost Guide.
              </h2>

              <p className="mt-6 text-white/75 text-base lg:text-lg leading-relaxed max-w-xl">
                The same numbers we use when we quote your home — honest ranges by flat
                type, by trade, plus a 12-point checklist to compare any quote you receive.
                Written by the Noah+Rayn team and Awi Rafael.
              </p>

              <div className="mt-6 max-w-xl rounded-xl border border-[color:var(--nr-accent)]/40 bg-[color:var(--nr-accent)]/10 px-5 py-4">
                <div
                  className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold"
                  style={{ color: "#E6A079" }}
                  data-testid="lead-magnet-promo-label"
                >
                  Limited-time bonus
                </div>
                <p
                  className="mt-2 text-white text-sm lg:text-base leading-relaxed"
                  data-testid="lead-magnet-promo"
                >
                  Stand to receive <span className="font-semibold">more exclusive discounts</span> if
                  you make a confirmed booking before <span className="font-semibold">end of 2026</span>.
                  Enquire more during your <span className="font-semibold">FREE consultation</span> with our team.
                </p>
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-4 max-w-2xl">
                {[
                  "Real 2026 cost ranges, 3-room to Executive",
                  "Per-trade pricing (hacking, tiling, carpentry…)",
                  "5 places to save without compromise",
                  "12-point quote-comparison checklist",
                ].map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-white/85"
                    data-testid={`lead-magnet-benefit-${i}`}
                  >
                    <CheckCircle2
                      size={18}
                      className="text-[color:var(--nr-accent)] shrink-0 mt-0.5"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex items-center gap-6 text-white/60 text-xs uppercase tracking-[0.2em]">
                <div className="flex items-center gap-2">
                  <FileText size={14} /> PDF · 1.2 MB
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen size={14} /> No newsletter spam
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-5 reveal">
              <div className="bg-white rounded-2xl p-7 lg:p-9 text-[color:var(--nr-ink)] shadow-2xl">
                {done ? (
                  <div
                    data-testid="lead-magnet-success"
                    className="text-center py-6"
                  >
                    <div className="w-14 h-14 rounded-full bg-[color:var(--nr-accent)]/10 text-[color:var(--nr-accent)] flex items-center justify-center mx-auto mb-5">
                      <Download size={22} />
                    </div>
                    <h3 className="font-serif-display text-3xl text-[color:var(--nr-ink)]">
                      Your guide is on its way.
                    </h3>
                    <p className="mt-3 text-sm text-[color:var(--nr-ink-soft)]">
                      The PDF should have started downloading.
                      <br />
                      If not, click below.
                    </p>
                    <a
                      data-testid="lead-magnet-redownload"
                      href={`${API}/leads/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 btn-primary rounded-full px-7 py-3.5 text-sm font-semibold"
                    >
                      <Download size={16} /> Download again
                    </a>
                    <button
                      onClick={() => {
                        setDone(false);
                        setName("");
                        setEmail("");
                        setPropertyType("");
                      }}
                      data-testid="lead-magnet-reset"
                      className="block mx-auto mt-4 text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)] hover:text-[color:var(--nr-ink)]"
                    >
                      Send to another email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="lead-magnet-form">
                    <BrandMark className="w-9 h-9 text-[color:var(--nr-accent)] mb-4" data-testid="lead-magnet-brandmark" />
                    <p className="overline mb-3">Get the guide</p>
                    <h3 className="font-serif-display text-3xl text-[color:var(--nr-ink)] leading-tight">
                      Where should we send it?
                    </h3>

                    <div className="mt-6 space-y-1">
                      <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Your name *
                      </label>
                      <input
                        data-testid="lead-magnet-name"
                        type="text"
                        className="nr-input"
                        placeholder="Jane Tan"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mt-4 space-y-1">
                      <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Email *
                      </label>
                      <input
                        data-testid="lead-magnet-email"
                        type="email"
                        className="nr-input"
                        placeholder="jane@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mt-4 space-y-1">
                      <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                        Where are you in the journey?
                      </label>
                      <select
                        data-testid="lead-magnet-property"
                        className="nr-input"
                        value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {PROPERTY_TYPES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="lead-magnet-submit"
                      className="mt-7 btn-primary w-full rounded-full px-6 py-4 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Preparing…" : (
                        <>
                          <Download size={16} />
                          Send Me the Guide
                        </>
                      )}
                    </button>

                    <p className="mt-4 text-[0.7rem] leading-relaxed text-[color:var(--nr-ink-soft)] text-center">
                      We'll only use your email to send the guide and the
                      occasional renovation tip. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
